import { useStudyPlan } from "../../../../../../app/contexts/study.context";
import { useSnackbar } from "../../../../../../app/contexts/snackbar.context";
import { applyParticipantTerminologyToExcelWorksheet } from "../../../../../../app/utils/participantTerminology";
import { formatDateForExport, NUMBER_FORMAT_2_DECIMALS } from "../../../../../../app/utils/process.excel.helper";
import { resolveSiteIdEffort } from "../../../../../../app/utils/siteIdEffort";
import ExcelJS from "exceljs";
import excelFile from "./plan_template.xlsx";

const DURATION = "Duration";
const RATES = "Rates";

export const useExportPlan = () => {
  const { studyPlan } = useStudyPlan();
  const { showSnackbar } = useSnackbar();

  // Function to extract country inputs data
  const extractCountryInputsData = (countries, studyPlanTypeDetail) => {
    const countryNames = [];
    const siteIdEfforts = [];
    const countryStartUpTimeFrames = [];
    const enrollmentRates = [];
    const numActivatedSitesLowerBounds = [];
    const numActivatedSitesUpperBounds = [];
    const numPatientsScreenedLowerBounds = [];
    const numPatientsScreenedUpperBounds = [];
    const screenFailRates = [];
    const numPatientsEnrolled = [];
    const dropOutRates = [];
    const numPatientsCompletingTreatment = [];
    const numPatientsCompletingFollowUp = [];

    countries.forEach((country) => {
      const siteGroups = country.siteGroups;
      siteGroups.forEach((siteGroup) => {
        countryNames.push(siteGroup.siteGroupName);
        countryStartUpTimeFrames.push(siteGroup.timeCountryStartUpSize);
        enrollmentRates.push((studyPlanTypeDetail === DURATION) ? "" : siteGroup.siteEnrollmentRateAtSIV);

        const sitesToActivate = siteGroup.parameters.find(
          (param) => param.parameterName === "SITES_TO_ACTIVATE",
        );
        const sitesLower = sitesToActivate?.lowerBound ?? null;
        const sitesUpper = sitesToActivate?.upperBound ?? null;
        numActivatedSitesLowerBounds.push(sitesLower);
        numActivatedSitesUpperBounds.push(sitesUpper);
        // Prefer UI Effort (timeSiteIdSize); derive only when blank so col C is a literal, not the template formula
        siteIdEfforts.push(
          resolveSiteIdEffort(siteGroup.timeSiteIdSize, sitesLower, sitesUpper),
        );
        numPatientsScreenedLowerBounds.push(
          siteGroup.parameters.find(
            (param) => param.parameterName === "SUBJECTS_TO_SCREEN",
          )?.lowerBound || null,
        );
        numPatientsScreenedUpperBounds.push(
          siteGroup.parameters.find(
            (param) => param.parameterName === "SUBJECTS_TO_SCREEN",
          )?.upperBound || null,
        );
        screenFailRates.push(siteGroup.expectedScreenFailureRate);
        numPatientsEnrolled.push(
          siteGroup.milestones.find(
            (milestone) =>
              milestone.milestoneName === "NUM_SUBJECTS_ENROLLED",
          )?.lowerBound || null,
        );
        dropOutRates.push(siteGroup.expectedTreatmentDropoutRate);
        numPatientsCompletingTreatment.push(
          siteGroup.milestones.find(
            (milestone) => milestone.milestoneName === "NUM_SUBJECTS_TREATED",
          )?.lowerBound || null,
        );
        numPatientsCompletingFollowUp.push(
          siteGroup.milestones.find(
            (milestone) =>
              milestone.milestoneName === "NUM_SUBJECTS_COMPLETED",
          )?.lowerBound || null,
        );
      });
    });

    return {
      countryNames,
      siteIdEfforts,
      countryStartUpTimeFrames,
      enrollmentRates,
      numActivatedSitesLowerBounds,
      numActivatedSitesUpperBounds,
      numPatientsScreenedLowerBounds,
      numPatientsScreenedUpperBounds,
      screenFailRates,
      numPatientsEnrolled,
      dropOutRates,
      numPatientsCompletingTreatment,
      numPatientsCompletingFollowUp,
    };
  };

  // Function to extract milestones data
  const extractMilestonesData = (countries) => {
    const siteIdFinishDates = [];
    const siteIdFinishComments = [];
    const regulatorySubmissionDates = [];
    const regulatorySubmissionComments = [];
    const firstSiteStartUpDates = [];
    const firstSiteStartUpComments = [];
    const firstSiteActivationDates = [];
    const firstSiteActivationComments = [];
    const firstPatientScreenedDates = [];
    const firstPatientScreenedComments = [];
    const firstPatientEnrolledDates = [];
    const firstPatientEnrolledComments = [];
    const lastPatientEnrolledDates = [];
    const lastPatientEnrolledComments = [];

    countries.forEach((country) => {
      const siteGroups = country.siteGroups;
      siteGroups.forEach((siteGroup) => {
        const siteIdFinishMilestone = siteGroup.milestones.find(
          (milestone) =>
            milestone.milestoneName === "DURATION_TO_SITE_ID_FINISH",
        );
        siteIdFinishDates.push(
          formatDateForExport(siteIdFinishMilestone?.milestoneDateMillis),
        );
        siteIdFinishComments.push(siteIdFinishMilestone?.comments || null);

        const regulatorySubmissionMilestone = siteGroup.milestones.find(
          (milestone) => milestone.milestoneName === "DURATION_TO_SUBMISSION",
        );
        regulatorySubmissionDates.push(
          formatDateForExport(regulatorySubmissionMilestone?.milestoneDateMillis),
        );
        regulatorySubmissionComments.push(
          regulatorySubmissionMilestone?.comments || null,
        );

        const firstSiteStartUpMilestone = siteGroup.milestones.find(
          (milestone) =>
            milestone.milestoneName ===
            "DURATION_TO_FIRST_REGULATORY_APPROVAL",
        );
        firstSiteStartUpDates.push(
          formatDateForExport(firstSiteStartUpMilestone?.milestoneDateMillis),
        );
        firstSiteStartUpComments.push(
          firstSiteStartUpMilestone?.comments || null,
        );

        const firstSiteActivationMilestone = siteGroup.milestones.find(
          (milestone) => milestone.milestoneName === "DURATION_TO_FSIV",
        );
        firstSiteActivationDates.push(
          formatDateForExport(firstSiteActivationMilestone?.milestoneDateMillis),
        );
        firstSiteActivationComments.push(
          firstSiteActivationMilestone?.comments || null,
        );

        const firstPatientScreenedMilestone = siteGroup.milestones.find(
          (milestone) => milestone.milestoneName === "DURATION_TO_FPS",
        );
        firstPatientScreenedDates.push(
          formatDateForExport(firstPatientScreenedMilestone?.milestoneDateMillis),
        );
        firstPatientScreenedComments.push(
          firstPatientScreenedMilestone?.comments || null,
        );

        const firstPatientEnrolledMilestone = siteGroup.milestones.find(
          (milestone) => milestone.milestoneName === "DURATION_TO_FPI",
        );
        firstPatientEnrolledDates.push(
          formatDateForExport(firstPatientEnrolledMilestone?.milestoneDateMillis),
        );
        firstPatientEnrolledComments.push(
          firstPatientEnrolledMilestone?.comments || null,
        );

        const lastPatientEnrolledMilestone = siteGroup.milestones.find(
          (milestone) => milestone.milestoneName === "DURATION_TO_LPI",
        );
        lastPatientEnrolledDates.push(
          formatDateForExport(lastPatientEnrolledMilestone?.milestoneDateMillis),
        );
        lastPatientEnrolledComments.push(
          lastPatientEnrolledMilestone?.comments || null,
        );
      });
    });

    return {
      siteIdFinishDates,
      siteIdFinishComments,
      regulatorySubmissionDates,
      regulatorySubmissionComments,
      firstSiteStartUpDates,
      firstSiteStartUpComments,
      firstSiteActivationDates,
      firstSiteActivationComments,
      firstPatientScreenedDates,
      firstPatientScreenedComments,
      firstPatientEnrolledDates,
      firstPatientEnrolledComments,
      lastPatientEnrolledDates,
      lastPatientEnrolledComments,
    };
  };

  // Function to extract activation profiles data
  const extractActivationProfilesData = (countries) => {
    const card1Date = [];
    const card1Percent = [];
    const card2Date = [];
    const card2Percent = [];
    const card3Date = [];
    const card3Percent = [];
    const card4Date = [];
    const card4Percent = [];
    const card5Date = [];
    const card5Percent = [];
    const card6Date = [];
    const card6Percent = [];
    const card7Date = [];
    const card7Percent = [];
    const card8Date = [];
    const card8Percent = [];
    const card9Date = [];
    const card9Percent = [];
    const card10Date = [];
    const card10Percent = [];
    const card11Date = [];
    const card11Percent = [];
    const overrideFl = [];
    const countryNames = [];

    countries.forEach((country) => {
      const siteGroups = country.siteGroups;
      countryNames.push(country.countryName);
      overrideFl.push(
        Number(country.siteGroups?.[0]?.activationProfiles?.[0]?.overrideFl) === 1 ? "Yes" : "No"
      );
      siteGroups.forEach((siteGroup) => {
        const activationProfiles = siteGroup.activationProfiles;
        for (let i = 0; i < 11; i++) {
          const profile = activationProfiles[i];
          switch (i) {
            case 0:
              card1Date.push(profile ?
                formatDateForExport(profile.siteActivationProfileDateMillis) : null,
              );
              card1Percent.push(profile ? profile.siteActivationProfilePct : null);
              break;
            case 1:
              card2Date.push(profile ?
                formatDateForExport(profile.siteActivationProfileDateMillis) : null,
              );
              card2Percent.push(profile ? profile.siteActivationProfilePct : null);
              break;
            case 2:
              card3Date.push(profile ?
                formatDateForExport(profile.siteActivationProfileDateMillis) : null,
              );
              card3Percent.push(profile ? profile.siteActivationProfilePct : null);
              break;
            case 3:
              card4Date.push(profile ?
                formatDateForExport(profile.siteActivationProfileDateMillis) : null,
              );
              card4Percent.push(profile ? profile.siteActivationProfilePct : null);
              break;
            case 4:
              card5Date.push(profile ?
                formatDateForExport(profile.siteActivationProfileDateMillis) : null,
              );
              card5Percent.push(profile ? profile.siteActivationProfilePct : null);
              break;
            case 5:
              card6Date.push(profile ?
                formatDateForExport(profile.siteActivationProfileDateMillis) : null,
              );
              card6Percent.push(profile ? profile.siteActivationProfilePct : null);
              break;
            case 6:
              card7Date.push(profile ?
                formatDateForExport(profile.siteActivationProfileDateMillis) : null,
              );
              card7Percent.push(profile ? profile.siteActivationProfilePct : null);
              break;
            case 7:
              card8Date.push(profile ?
                formatDateForExport(profile.siteActivationProfileDateMillis) : null,
              );
              card8Percent.push(profile ? profile.siteActivationProfilePct : null);
              break;
            case 8:
              card9Date.push(profile ?
                formatDateForExport(profile.siteActivationProfileDateMillis) : null,
              );
              card9Percent.push(profile ? profile.siteActivationProfilePct : null);
              break;
            case 9:
              card10Date.push(profile ?
                formatDateForExport(profile.siteActivationProfileDateMillis) : null,
              );
              card10Percent.push(profile ? profile.siteActivationProfilePct : null);
              break;
            case 10:
              card11Date.push(profile ?
                formatDateForExport(profile.siteActivationProfileDateMillis) : null,
              );
              card11Percent.push(profile ? profile.siteActivationProfilePct : null);
              break;
            default:
              break;
          }
        }
      });
    });

    return {
      card1Date,
      card1Percent,
      card2Date,
      card2Percent,
      card3Date,
      card3Percent,
      card4Date,
      card4Percent,
      card5Date,
      card5Percent,
      card6Date,
      card6Percent,
      card7Date,
      card7Percent,
      card8Date,
      card8Percent,
      card9Date,
      card9Percent,
      card10Date,
      card10Percent,
      card11Date,
      card11Percent,
      overrideFl,
      countryNames,
    };
  };

  // Function to extract enrollment variations data
  const extractEnrollmentVariationsData = (countries) => {
    const enrollmentJan = [];
    const enrollmentFeb = [];
    const enrollmentMar = [];
    const enrollmentApr = [];
    const enrollmentMay = [];
    const enrollmentJun = [];
    const enrollmentJul = [];
    const enrollmentAug = [];
    const enrollmentSep = [];
    const enrollmentOct = [];
    const enrollmentNov = [];
    const enrollmentDec = [];

    countries.forEach((country) => {
      const siteGroups = country.siteGroups;
      siteGroups.forEach((siteGroup) => {
        const enrollmentVariations = siteGroup.enrollmentVariations;
        enrollmentVariations.forEach((variation) => {
          const month = variation.enrollmentSeasonalMonth;
          const value = variation.enrollmentSeasonalVariation;

          switch (month) {
            case 0:
              enrollmentJan.push(value);
              break;
            case 1:
              enrollmentFeb.push(value);
              break;
            case 2:
              enrollmentMar.push(value);
              break;
            case 3:
              enrollmentApr.push(value);
              break;
            case 4:
              enrollmentMay.push(value);
              break;
            case 5:
              enrollmentJun.push(value);
              break;
            case 6:
              enrollmentJul.push(value);
              break;
            case 7:
              enrollmentAug.push(value);
              break;
            case 8:
              enrollmentSep.push(value);
              break;
            case 9:
              enrollmentOct.push(value);
              break;
            case 10:
              enrollmentNov.push(value);
              break;
            case 11:
              enrollmentDec.push(value);
              break;
            default:
              break;
          }
        });
      });
    });

    return {
      enrollmentJan,
      enrollmentFeb,
      enrollmentMar,
      enrollmentApr,
      enrollmentMay,
      enrollmentJun,
      enrollmentJul,
      enrollmentAug,
      enrollmentSep,
      enrollmentOct,
      enrollmentNov,
      enrollmentDec,
    };
  };

  // Function to extract site fatigue data
  const extractSiteFatigueData = (countries) => {
    const eventualSiteEnrollmentDaysList = [];
    const eventualSiteEnrollmentRateList = [];

    countries.forEach((country) => {
      const siteGroups = country.siteGroups;
      siteGroups.forEach((siteGroup) => {
        const eventualSiteEnrollmentDays = siteGroup.eventualSiteEnrollmentDays;
        const eventualSiteEnrollmentRate = siteGroup.eventualSiteEnrollmentRate;

        eventualSiteEnrollmentDaysList.push(eventualSiteEnrollmentDays);
        eventualSiteEnrollmentRateList.push(eventualSiteEnrollmentRate);
      });
    });

    return {
      eventualSiteEnrollmentDaysList,
      eventualSiteEnrollmentRateList,
    };
  };

  // Function to load Excel template
  const loadExcelTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(
      await fetch(excelFile).then((res) => res.arrayBuffer()),
    );
    return { workbook, worksheet: workbook.getWorksheet(1) };
  };

  // Function to populate study plan details in worksheet
  const populateStudyPlanDetails = (worksheet, studyPlanTypeDetail) => {
    // Set the value of cell C1
    worksheet.getCell("C1").value = studyPlanTypeDetail;

    // Set study plan details
    worksheet.getCell("D5").value = studyPlan.studyPlanName;
    worksheet.getCell("D6").value = studyPlan.studyPlanDescr;
    worksheet.getCell("J5").value = formatDateForExport(
      studyPlan.croProjectStartDateMillis,
    );
    worksheet.getCell("J6").value = formatDateForExport(
      studyPlan.finalProtocolStartDateMillis,
    );
    worksheet.getCell("D8").value = studyPlan.overallEnrollmentDuration || "";
    worksheet.getCell("D9").value = studyPlan.enrollmentRate;
    worksheet.getCell("D9").numFmt = NUMBER_FORMAT_2_DECIMALS;

    // Set the total number of sites to activate (SITES_TO_ACTIVATE parameter) only for Rates study type
    if (studyPlanTypeDetail === RATES) {
      const sitesToActivate = studyPlan.parameters?.find(
        (param) => param.parameterName === "SITES_TO_ACTIVATE",
      );
      worksheet.getCell("D10").value = sitesToActivate?.lowerBound || null;
    }

    worksheet.getCell("D13").value = studyPlan.screeningPeriodDuration;
    worksheet.getCell("D14").value = studyPlan.treatmentDuration;
    worksheet.getCell("D15").value = studyPlan.followUpDuration;

    // Set the total number of subjects to screen (SUBJECTS_TO_SCREEN parameter)
    const subjectsToScreen = studyPlan.parameters?.find(
      (param) => param.parameterName === "SUBJECTS_TO_SCREEN",
    );
    worksheet.getCell("D17").value = subjectsToScreen?.lowerBound || null;

    // Set the total number of subjects to enroll from milestones
    const subjectsToEnroll = studyPlan.milestones?.find(
      (milestone) => milestone.milestoneName === "NUM_SUBJECTS_ENROLLED",
    );
    worksheet.getCell("D18").value = subjectsToEnroll?.lowerBound || null;

    const subjectsToTreat = studyPlan.milestones?.find(
      (milestone) => milestone.milestoneName === "NUM_SUBJECTS_TREATED",
    );
    worksheet.getCell("D19").value = subjectsToTreat?.lowerBound || null;

    const subjectsToComplete = studyPlan.milestones?.find(
      (milestone) => milestone.milestoneName === "NUM_SUBJECTS_COMPLETED",
    );
    worksheet.getCell("D20").value = subjectsToComplete?.lowerBound || null;

    worksheet.getCell("D23").value = studyPlan.pauseEnrollmentAfterPatient;
    worksheet.getCell("D24").value = studyPlan.pauseEnrollmentDays;

    worksheet.getCell("H18").value = studyPlan.screenFailRate;
    worksheet.getCell("H18").numFmt = NUMBER_FORMAT_2_DECIMALS;
    worksheet.getCell("H19").value = studyPlan.dropOutRate;
    worksheet.getCell("H19").numFmt = NUMBER_FORMAT_2_DECIMALS;
  };

  // Function to populate country inputs data in worksheet
  const populateCountryInputsData = (worksheet, countryInputsData) => {
    const { countryNames, siteIdEfforts, ...restData } = countryInputsData;
    
    // Set the country names in cells from B29 to B58
    const startRow = 29;
    countryNames.forEach((countryName, index) => {
      const cellAddress = `B${startRow + index}`;
      worksheet.getCell(cellAddress).value = countryName;
    });

    // Template col C uses shared formulas. Clear clones bottom-up, then master,
    // before writing UI Effort literals — otherwise ExcelJS fails on write with
    // "Shared Formula master must exist above and or left of clone".
    const siteIdEffortEndRow = 58;
    for (let row = siteIdEffortEndRow; row >= startRow; row--) {
      const cell = worksheet.getCell(`C${row}`);
      cell.value = null;
    }
    (siteIdEfforts || []).forEach((effort, index) => {
      if (effort == null || effort === "") return;
      worksheet.getCell(`C${startRow + index}`).value = effort;
    });

    // Set the data in cells from D29 onwards
    const columns = ["D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N"];
    const lowerAndUpperBoundColumns = ["F", "G"];
    const data = [
      restData.countryStartUpTimeFrames,
      restData.enrollmentRates,
      restData.numActivatedSitesLowerBounds,
      restData.numActivatedSitesUpperBounds,
      restData.numPatientsScreenedLowerBounds,
      restData.numPatientsScreenedUpperBounds,
      restData.screenFailRates,
      restData.numPatientsEnrolled,
      restData.dropOutRates,
      restData.numPatientsCompletingTreatment,
      restData.numPatientsCompletingFollowUp,
    ];
    data.forEach((dataArray, dataIndex) => {
      dataArray.forEach((value, index) => {
        const cellAddress = `${columns[dataIndex]}${startRow + index}`;
        const cell = worksheet.getCell(cellAddress);
        cell.value = value;
        if (lowerAndUpperBoundColumns.includes(columns[dataIndex])) {
          cell.numFmt = "";
        }
      });
    });
  };

  // Function to populate milestones data in worksheet
  const populateMilestonesData = (worksheet, milestonesData) => {
    const milestoneStartRow = 64;
    // Set the milestone dates and comments in the next columns
    const milestoneColumns = [
      "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O",
    ];
    const milestoneDataArrays = [
      milestonesData.siteIdFinishDates,
      milestonesData.siteIdFinishComments,
      milestonesData.regulatorySubmissionDates,
      milestonesData.regulatorySubmissionComments,
      milestonesData.firstSiteStartUpDates,
      milestonesData.firstSiteStartUpComments,
      milestonesData.firstSiteActivationDates,
      milestonesData.firstSiteActivationComments,
      milestonesData.firstPatientScreenedDates,
      milestonesData.firstPatientScreenedComments,
      milestonesData.firstPatientEnrolledDates,
      milestonesData.firstPatientEnrolledComments,
      milestonesData.lastPatientEnrolledDates,
    ];
    milestoneDataArrays.forEach((milestoneArray, milestoneIndex) => {
      milestoneArray.forEach((value, index) => {
        const cellAddress = `${milestoneColumns[milestoneIndex]}${
          milestoneStartRow + index
        }`;
        worksheet.getCell(cellAddress).value = value;
      });
    });
  };

  // Function to populate activation profiles data in worksheet
  const populateActivationProfilesData = (worksheet, activationProfilesData) => {
    const cardsStartRow = 99;
    // Set the milestone dates and comments in the next columns
    const cardColumns = [
      "B", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y",
    ];
    const cardData = [
      activationProfilesData.overrideFl,
      activationProfilesData.card1Percent,
      activationProfilesData.card1Date,
      activationProfilesData.card2Percent,
      activationProfilesData.card2Date,
      activationProfilesData.card3Percent,
      activationProfilesData.card3Date,
      activationProfilesData.card4Percent,
      activationProfilesData.card4Date,
      activationProfilesData.card5Percent,
      activationProfilesData.card5Date,
      activationProfilesData.card6Percent,
      activationProfilesData.card6Date,
      activationProfilesData.card7Percent,
      activationProfilesData.card7Date,
      activationProfilesData.card8Percent,
      activationProfilesData.card8Date,
      activationProfilesData.card9Percent,
      activationProfilesData.card9Date,
      activationProfilesData.card10Percent,
      activationProfilesData.card10Date,
      activationProfilesData.card11Percent,
      activationProfilesData.card11Date,
    ];

    cardData.forEach((cardArray, cardIndex) => {
      cardArray.forEach((value, index) => {
        const rowNumber = cardsStartRow + index;
        const cellAddress = `${cardColumns[cardIndex]}${rowNumber}`;
        const cell = worksheet.getCell(cellAddress);
        cell.value = value;
        cell.numFmt = "";
      });
    });
  };

  // Function to populate enrollment variations data in worksheet
  const populateEnrollmentVariationsData = (worksheet, enrollmentVariationsData) => {
    const enrollmentStartRow = 132;
    // Set the milestone dates and comments in the next columns
    const enrollmentColumns = [
      "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N",
    ];
    const enrollmentData = [
      enrollmentVariationsData.enrollmentJan,
      enrollmentVariationsData.enrollmentFeb,
      enrollmentVariationsData.enrollmentMar,
      enrollmentVariationsData.enrollmentApr,
      enrollmentVariationsData.enrollmentMay,
      enrollmentVariationsData.enrollmentJun,
      enrollmentVariationsData.enrollmentJul,
      enrollmentVariationsData.enrollmentAug,
      enrollmentVariationsData.enrollmentSep,
      enrollmentVariationsData.enrollmentOct,
      enrollmentVariationsData.enrollmentNov,
      enrollmentVariationsData.enrollmentDec,
    ];
    enrollmentData.forEach((enrollmentArray, enrollmentIndex) => {
      enrollmentArray.forEach((value, index) => {
        const cellAddress = `${enrollmentColumns[enrollmentIndex]}${
          enrollmentStartRow + index
        }`;
        worksheet.getCell(cellAddress).value = value;
      });
    });
  };

  // Function to populate site fatigue data in worksheet
  const populateSiteFatigueData = (worksheet, siteFatigueData) => {
    const fatigueStartRow = 165;
    // Set the milestone dates and comments in the next columns
    const fatigueColumns = ["C", "D"];
    const fatigueData = [
      siteFatigueData.eventualSiteEnrollmentDaysList,
      siteFatigueData.eventualSiteEnrollmentRateList,
    ];
    fatigueData.forEach((fatigueArray, fatigueIndex) => {
      fatigueArray.forEach((value, index) => {
        const cellAddress = `${fatigueColumns[fatigueIndex]}${
          fatigueStartRow + index
        }`;
        worksheet.getCell(cellAddress).value = value;
      });
    });
  };

  // Function to generate and download Excel file
  const generateAndDownloadExcel = async (workbook) => {
    // Generate the updated Excel file
    const excelBuffer = await workbook.xlsx.writeBuffer();
    // Create a Blob from the Excel buffer
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    // Create a download link and trigger the download
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${studyPlan.studyName}_study_plan.xlsx`;
    link.click();
  };

  const exportPlan = async () => {
    try {
      showSnackbar(`Loading data...`, "success");
      
      // Get basic data
      const countries = studyPlan["countries"];
      const studyPlanTypeDetail = (Number(studyPlan["studyPlanType"]) === 1) ? RATES : DURATION;

      // Extract all data using helper functions
      const countryInputsData = extractCountryInputsData(countries, studyPlanTypeDetail);
      const milestonesData = extractMilestonesData(countries);
      const activationProfilesData = extractActivationProfilesData(countries);
      const enrollmentVariationsData = extractEnrollmentVariationsData(countries);
      const siteFatigueData = extractSiteFatigueData(countries);

      // Load Excel template and map template labels only (before user-entered values)
      const { workbook, worksheet } = await loadExcelTemplate();
      applyParticipantTerminologyToExcelWorksheet(worksheet);

      // Populate worksheet with all data
      populateStudyPlanDetails(worksheet, studyPlanTypeDetail);
      populateCountryInputsData(worksheet, countryInputsData);
      populateMilestonesData(worksheet, milestonesData);
      populateActivationProfilesData(worksheet, activationProfilesData);
      populateEnrollmentVariationsData(worksheet, enrollmentVariationsData);
      populateSiteFatigueData(worksheet, siteFatigueData);

      // Generate and download the Excel file
      await generateAndDownloadExcel(workbook);
      
      showSnackbar(
        `Data has been loaded and saved into the excel template.`,
        "success",
      );
    } catch (error) {
      console.error("Error fetching study plan:", error);
      showSnackbar(`Error fetching study plan: ${error}`, "error");
    }
  };
  
  return exportPlan;
};
